<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import accountTemplatesAPI from 'dashboard/api/accountTemplates';
import Button from 'dashboard/components-next/button/Button.vue';
import ApplicationReport from './ApplicationReport.vue';

const props = defineProps({ subAccount: { type: Object, required: true } });
const emit = defineEmits(['close']);
const { t } = useI18n();

const FINAL = ['done', 'done_with_warnings', 'failed'];
const templates = ref([]);
const templateId = ref(null);
const policy = ref('skip');
const applying = ref(false);
const application = ref(null);
let timer = null;

const poll = async id => {
  const { data } = await accountTemplatesAPI.application(id);
  application.value = data;
  if (!FINAL.includes(data.status)) timer = setTimeout(() => poll(id), 2000);
};

const apply = async () => {
  applying.value = true;
  try {
    const { data } = await accountTemplatesAPI.apply({
      account_template_id: templateId.value,
      sub_account_id: props.subAccount.id,
      conflict_policy: policy.value,
    });
    await poll(data.id);
  } catch (error) {
    useAlert(t('REVENDA.TEMPLATES.FAILED'));
  } finally {
    applying.value = false;
  }
};

const retry = async () => {
  await accountTemplatesAPI.retryFailed(application.value.id);
  application.value = { ...application.value, status: 'running' };
  poll(application.value.id);
};

onMounted(async () => {
  const { data } = await accountTemplatesAPI.get();
  templates.value = (data.templates || []).filter(
    item => item.status === 'ready'
  );
  templateId.value = templates.value[0]?.id || null;
});
onBeforeUnmount(() => clearTimeout(timer));
</script>

<template>
  <woot-modal show :on-close="() => emit('close')">
    <div class="flex flex-col gap-4 p-6">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('REVENDA.TEMPLATES.APPLY_TITLE', { name: subAccount.name }) }}
      </h3>
      <template v-if="!application">
        <p v-if="!templates.length" class="text-sm text-n-slate-11">
          {{ t('REVENDA.TEMPLATES.EMPTY') }}
        </p>
        <template v-else>
          <select v-model="templateId">
            <option
              v-for="template in templates"
              :key="template.id"
              :value="template.id"
            >
              {{ template.name }}
            </option>
          </select>
          <fieldset class="flex flex-col gap-1 text-sm text-n-slate-11">
            <legend class="mb-1 font-medium text-n-slate-12">
              {{ t('REVENDA.TEMPLATES.CONFLICT') }}
            </legend>
            <label class="flex items-center gap-2">
              <input v-model="policy" type="radio" value="skip" />
              {{ t('REVENDA.TEMPLATES.CONFLICT_SKIP') }}
            </label>
            <label class="flex items-center gap-2">
              <input v-model="policy" type="radio" value="copy" />
              {{ t('REVENDA.TEMPLATES.CONFLICT_COPY') }}
            </label>
          </fieldset>
          <Button
            :label="t('REVENDA.TEMPLATES.APPLY')"
            :disabled="!templateId"
            :is-loading="applying"
            @click="apply"
          />
        </template>
      </template>
      <p
        v-else-if="
          !['done', 'done_with_warnings', 'failed'].includes(application.status)
        "
        class="text-sm text-n-slate-11"
      >
        {{ t('REVENDA.TEMPLATES.REPORT.RUNNING') }}
      </p>
      <ApplicationReport
        v-else
        :report="application.report || []"
        @retry="retry"
      />
    </div>
  </woot-modal>
</template>
